import {useState, useEffect, useMemo, useCallback} from 'react';
import {Checkbox} from "antd";
import MyText from "../../../../../../components/UI/Text/MyText";
import {useTranslation} from "react-i18next";

export const useCompetenceMatrix = (data, connectionsRef) => {
    const {t} = useTranslation();
    const [connections, setConnections] = useState({});

    const getConnectionKey = useCallback((competenceId, resultId) => {
        return `${competenceId}_${resultId}`;
    }, []);

    useEffect(() => {
        if (data?.results) {
            const initialConnections = {};
            data.results.forEach(result => {
                result.competences?.forEach(competence => {
                    const key = getConnectionKey(competence.id, result.id);
                    initialConnections[key] = true;
                });
            });
            setConnections(initialConnections);
            if (connectionsRef) {
                connectionsRef.current = initialConnections;
            }
        }
    }, [data, connectionsRef, getConnectionKey]);

    const handleCheckboxChange = useCallback((competenceId, resultId, checked) => {
        const key = getConnectionKey(competenceId, resultId);
        setConnections(prev => {
            const newConnections = {
                ...prev,
                [key]: checked
            };
            if (connectionsRef) {
                connectionsRef.current = newConnections;
            }
            return newConnections;
        });
    }, [getConnectionKey, connectionsRef]);

    const isChecked = useCallback((competenceId, resultId) => {
        const key = getConnectionKey(competenceId, resultId);
        return connections[key] || false;
    }, [connections, getConnectionKey]);

    const baseColumn = useMemo(() => ({
        title: <MyText center>{t('microQ.formCompetence')}</MyText>,
        width: 200,
        dataIndex: 'competenceName',
        key: 'competenceName',
        fixed: 'left',
        render: (text, record) => (
            <div>
                <strong>ПК{record.competenceNumber}</strong>
            </div>
        )
    }), [t]);

    const resultColumns = useMemo(() => {
        return data?.results
            ?.filter(lesson => lesson.name.number !== null)
            ?.sort((a, b) => a.name.number - b.name.number)
            ?.map(result => ({
                title: <MyText center black>PO{result.name.number}</MyText>,
                dataIndex: result.id,
                key: result.id,
                width: 100,
                align: 'center',
                render: (_, record) => (
                    <div style={{textAlign: 'center', padding: '8px'}}>
                        <Checkbox
                            checked={isChecked(record.competenceId, result.id)}
                            onChange={(e) => handleCheckboxChange(record.competenceId, result.id, e.target.checked)}
                        />
                    </div>
                )
            })) || [];
    }, [data, isChecked, handleCheckboxChange]);

    const columns = useMemo(() => [baseColumn, ...resultColumns], [baseColumn, resultColumns]);

    const dataSource = useMemo(() => {
        return data?.competences?.list?.map((competence) => ({
            key: competence.id,
            competenceId: competence.id,
            competenceNumber: competence.name.number,
        })) || [];
    }, [data]);

    return {
        connections,
        columns,
        dataSource
    };
};
